import { useMutation } from '@apollo/react-hooks';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { LINK_ISSUES_EXPERIENCE } from '../../common/constants.tsx';
import { createDependencyExperience } from '../../common/experiences.tsx';
import { useIssuesMap } from '../../common/utils/use-issues-map.tsx';
import { useUpdateIssueLinksInCache } from '../update-dependencies-in-cache/index.tsx';
import type {
	CreateJWMDependencyTimelineView,
	CreateJWMDependencyTimelineViewVariables,
} from './__generated_apollo__/CreateJWMDependencyTimelineView';
import { CREATE_DEPENDENCY_TIMELINE_MUTATION } from './gql.tsx';
import type { UseCreateDependencyInput, UseCreateDependencyReturn } from './types.tsx';
import { createOptimisticLinkItems } from './utils.tsx';

export const fieldIdsToReturn = ['issuelinks'] as const;

const LOCATION = 'timeline create dependency service';
const QUERY_NAME = 'createDependencyTimelineView';

const findMutationBadRequestError = (data?: CreateJWMDependencyTimelineView) =>
	data?.linkJwmItems?.errors?.find((e) => e.message);

export const useCreateDependency = (): UseCreateDependencyReturn => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { updateIssueLinksInCache } = useUpdateIssueLinksInCache();
	const [{ issuesMap }] = useIssuesMap();

	const startExperience = useExperienceStart(LINK_ISSUES_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(LINK_ISSUES_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(LINK_ISSUES_EXPERIENCE);

	const [createDependency, { data, loading, error }] = useMutation<
		CreateJWMDependencyTimelineView,
		CreateJWMDependencyTimelineViewVariables
	>(CREATE_DEPENDENCY_TIMELINE_MUTATION, {
		onError: (apolloError) => {
			fireErrorAnalytics({
				meta: {
					id: QUERY_NAME,
					packageName: 'jiraBusinessTimeline',
					teamName: 'jira-yurree',
				},
				error: apolloError,
				attributes: { message: 'Failed to create dependency in timeline view' },
				sendToPrivacyUnsafeSplunk: true,
			});
			markExperienceFailed(LOCATION, apolloError);
			createDependencyExperience.failure({
				metadata: {
					error: apolloError.message,
					location: LOCATION,
				},
			});
		},
		onCompleted: (apolloData) => {
			const mutationBadRequestError = findMutationBadRequestError(apolloData);
			if (mutationBadRequestError) {
				const message = mutationBadRequestError.message ?? 'MutationBadRequestError';
				markExperienceFailed(LOCATION, Error(message));
				createDependencyExperience.failure({
					metadata: {
						error: message,
						location: LOCATION,
					},
				});
			} else {
				fireOperationalAnalytics(createAnalyticsEvent({}), `${QUERY_NAME} succeeded`, {});
				markExperienceSuccess();
				createDependencyExperience.success();
			}
		},
	});

	const create = async ({ input }: { input: UseCreateDependencyInput }) => {
		startExperience();
		createDependencyExperience.start();

		const optimisticResponse = {
			linkJwmItems: createOptimisticLinkItems({
				inwardIssueId: input.inwardIssueId,
				outwardIssueId: input.outwardIssueId,
				issuesMap,
			}),
		};

		const response = await createDependency({
			variables: {
				input,
				fieldIdsToReturn: [],
			},
			optimisticResponse,
			update: (proxy, result) => {
				const linkJwmItems = result.data?.linkJwmItems;
				if (linkJwmItems) {
					// update both issues (dependency and dependee) in Apollo cache with newly created links
					updateIssueLinksInCache({
						issueId: linkJwmItems.inwardItem?.issueId,
						issueLinks: linkJwmItems.inwardItem?.issueLinks,
					});
					updateIssueLinksInCache({
						issueId: linkJwmItems.outwardItem?.issueId,
						issueLinks: linkJwmItems.outwardItem?.issueLinks,
					});
				}
			},
		});

		const mutationError = findMutationBadRequestError(response.data);
		if (mutationError) {
			throw new ValidationError(mutationError.message ?? '');
		}
	};

	return {
		loading,
		// @ts-expect-error - Type 'ApolloError | CreateJWMDependencyTimelineView_linkJwmItems_errors | undefined' is not assignable to type 'ApolloError | undefined'.
		// if there are any errors from Apollo, surface that first
		// otherwise if there are errors from bad request, use that
		error: error || findMutationBadRequestError(data),
		create,
	};
};
